/* eslint-disable no-nested-ternary */
import { forwardRef } from 'react'
import OptionHOC from '@platform/react/hoc/autocomplete/option'
import { Box, Grid } from '@mui/material'
import AvatarImage from 'ui/Element/Image/Avatar'
import Text from 'ui/Element/Text'
import Chip from 'ui/Element/Text/Chip'
import { getSubLabel } from 'lib/util/string'
import { getDocumentationIcon } from 'lib/util/icon'

const styles = theme => ({
  option: {
    gap: 12,
    alignItems: 'center',
  },
  subLabel: {
    gap: 4,
    flexWrap: 'nowrap',
    display: 'flex!important',
  },
  icon: {
    width: '3rem',
    height: '3rem',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  text: {
    ...theme.typography['14/medium'],
    '& strong': {
      backgroundColor: theme.palette.success[50],
    },
  },
  badge: {
    width: '2.5rem',
  },
  badgeContent: {
    ...theme.typography['12/bold'],
    minWidth: 0,
    top: '0.25rem',
    right: '0.25rem',
    width: '1.125rem',
    height: '1.125rem',
    border: `0.125rem solid ${theme.palette.white.main}`,
  },
})

/**
 * Displays a Docchunk for the {@link Autocomplete} component. Will display a {@link Chip}
 * at the bottom with all the products where the doc chunk was found.
 *
 * @param {Object} classes  styling for the option
 * @param {Object} props    props for the option
 *
 * @returns {*}
 */
const DocChunkOption = (classes, props = {}) => {
  const { option, labels } = props || {}
  const { mentions } = labels

  const icon = getDocumentationIcon({
    name: option.value?.documentationType,
    nodeType: 'documentation',
  })

  const {
    value: {
      more = 0,
      image = null,
      highlights: { content = [] },
      product,
      label,
    } = {},
  } = option || {}

  const ForwardedChip = forwardRef((chipProps, ref) => <Chip forwardedRef={ref} {...chipProps} />)
  const productLabels = product.map((item, index) => (
    <ForwardedChip
      key={index}
      value={item}
      size={'tiny'}
      color={'pending.700'}
      backgroundColor={'pending.50'}
    />
  ))

  const attachment = image?.attachment?.value?.name
    ? { ...image.attachment, key: image?.value?.attachmentId || image.id, api: { ref: 'equipment' } }
    : { value: { name: '' } }

  return (
    <Grid
      container
      direction={'row'}
      flexWrap={'nowrap'}
      className={classes.option}
    >
      <Grid
        item
        xs={'auto'}
        className={classes.icon}
      >
        <AvatarImage
          attachment={attachment}
          shape={'circular'}
          icon={{
            source: icon || {},
            name: 'document',
            color: 'text.regular',
            background: 'gray.960',
          }}
        />
      </Grid>
      <Grid
        item
        xs={'auto'}
        sx={{ flexShrink: '1!important' }}
      >
        <Grid
          container
          height={'100%'}
          direction={'column'}
          alignItems={'flex-start'}
          justifyContent={'center'}
        >
          <Text
            maxLines={more > 0 ? 1 : 2}
            {...content?.[0] ? {
              component: 'div',
            } : {
              value: label,
            } }
            variant={'14/bold'}
            color={'black.main'}
          >
            {content?.[0] && <Box
              className={classes.text}
              dangerouslySetInnerHTML={{ __html: content[0] }}
            />}
          </Text>
          {productLabels?.length && (
            <Text
              maxLines={1}
              component={'div'}
              value={productLabels}
              className={classes.subLabel}
            />
          )}
          {more > 0 && (
            <Grid
              item
              container
              ms={'auto'}
              className={classes.subLabel}
              sx={{ marginTop: '0.25rem' }}
            >
              {getSubLabel(Text, [`+${more} ${mentions}`, <Text color={'black.main'} variant={'12/medium'} value={`"${label}"`}/>], ' ', { component: 'div' })}
            </Grid>
          )}
        </Grid>
      </Grid>
    </Grid>
  )
}

export default OptionHOC(DocChunkOption, styles)
